import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import { mapTeacherId, unmapTeacherId } from '../utils/teacherIdMapper';
import { 
  FaStar, 
  FaMapMarkerAlt, 
  FaClock, 
  FaDollarSign, 
  FaGraduationCap, 
  FaBook, 
  FaHeart, 
  FaEye, 
  FaCalendarAlt,
  FaGlobe,
  FaLanguage,
  FaCertificate,
  FaUsers,
  FaCheckCircle,
  FaPlay,
  FaLinkedin,
  FaTwitter,
  FaInstagram,
  FaEnvelope,
  FaPhone,
  FaArrowLeft,
  FaShare,
  FaFlag,
  FaClock as FaClockIcon,
  FaCalendarCheck,
  FaVideo,
  FaHeadset,
  FaChalkboardTeacher,
  FaUserFriends,
  FaAward,
  FaTrophy,
  FaMedal
} from 'react-icons/fa';

interface Teacher {
  _id: string;
  userId: {
    username: string;
    firstName: string;
    lastName: string;
  };
  basicInfo: {
    fullName: string;
    email: string;
    country: string;
    languages: string[];
    profilePicture?: string;
  };
  teachingExperience: {
    yearsTeaching: number;
    certifications?: string[];
    resume?: string;
    education?: string;
  };
  teachingOffer: {
    teachingStyle: string;
    targetStudents: string[];
    preferredSchedule: {
      days: string[];
      timeSlots: string[];
      timezone: string;
    };
  };
  rates: {
    hourlyRate: number;
    currency: string;
    offersFreeTrial: boolean;
    packages?: Array<{
      name: string;
      sessions: number;
      price: number;
      description: string;
    }>;
  };
  demoLesson?: {
    videoUrl?: string;
    description?: string;
  };
  profile: {
    bio: string;
    subjects: string[];
    specializations: string[];
    availability: {
      isAvailable: boolean;
      nextAvailableDate?: Date;
    };
  };
  stats: {
    totalSessions: number;
    totalStudents: number;
    averageRating: number;
    totalReviews: number;
    totalEarnings: number;
  };
  reviews: Array<{
    studentId: {
      username: string;
      firstName: string;
      lastName: string;
    };
    rating: number;
    comment: string;
    sessionId: string;
    createdAt: Date;
  }>;
  createdAt: Date;
  updatedAt: Date;
  approvedAt?: Date;
}

const TeacherProfile: React.FC = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'about' | 'reviews' | 'schedule'>('about');
  const [isFavorite, setIsFavorite] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Version for cache busting
  const VERSION = '2025-09-05-v2';

  useEffect(() => {
    if (teacherId) {
      fetchTeacherProfile();
    }
  }, [teacherId]);

  useEffect(() => {
    // Check if teacher is in favorites
    const favorites = JSON.parse(localStorage.getItem('teacher-favorites') || '[]');
    setIsFavorite(favorites.includes(teacherId));
  }, [teacherId]);

  // Mock teacher data for demonstration
  const mockTeachers: { [key: string]: Teacher } = {
    '1': {
      _id: '1',
      userId: { username: 'negin', firstName: 'Negin', lastName: 'Nickparsa' },
      basicInfo: {
        fullName: 'Negin Nickparsa',
        email: 'negin.nickparsa@aminepersia.com',
        country: 'Canada',
        languages: ['Persian (Native)', 'English (Fluent)', 'French (Advanced)', 'Turkish (Intermediate)'],
        profilePicture: '/images/profiles/negin-nickparsa.jpg'
      },
      teachingExperience: {
        yearsTeaching: 6,
        certifications: [
          'Bachelor\'s in Education, University of Toronto',
          'TESOL Certified',
          'Persian Language Teaching Certificate',
          'Child and Youth Education Specialist',
          'Multilingual Education Certificate'
        ],
        resume: 'https://aminepersia-9902f.web.app/resumes/negin-nickparsa.pdf'
      },
      teachingOffer: {
        teachingStyle: 'I specialize in making Persian learning fun and accessible for all ages. My approach uses games, songs, stories, and interactive activities to create an engaging learning environment. I believe in building confidence through positive reinforcement and gradual progression.',
        targetStudents: ['Beginner', 'Intermediate', 'Children', 'Families', 'Young learners', 'Seniors'],
        preferredSchedule: {
          days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
          timeSlots: ['09:00-11:00', '14:00-16:00', '18:00-20:00'],
          timezone: 'America/Toronto'
        }
      },
      rates: {
        hourlyRate: 28,
        currency: 'USD',
        offersFreeTrial: true,
        packages: [
          {
            name: 'Family Package',
            sessions: 6,
            price: 150,
            description: 'Fun Persian learning for the whole family - games, songs, and cultural activities'
          },
          {
            name: 'Children\'s Persian',
            sessions: 10,
            price: 250,
            description: 'Age-appropriate Persian lessons with interactive games and storytelling'
          },
          {
            name: 'Beginner\'s Journey',
            sessions: 12,
            price: 300,
            description: 'Complete beginner course with focus on conversation and basic grammar'
          }
        ]
      },
      profile: {
        bio: 'I am passionate about teaching Persian to learners of all ages, with special expertise in children\'s education and family learning. My background in education and multilingual teaching allows me to create engaging, effective lessons that make Persian accessible to everyone.',
        subjects: ['Beginner Persian', 'Children\'s Persian', 'Family Persian', 'Persian for Young Learners', 'Cultural Activities', 'Persian Songs & Stories'],
        specializations: ['Early Childhood Persian', 'Family Language Learning', 'Multilingual Education', 'Cultural Immersion', 'Interactive Learning'],
        availability: {
          isAvailable: true
        }
      },
      stats: {
        totalSessions: 720,
        totalStudents: 45,
        averageRating: 4.9,
        totalReviews: 98,
        totalEarnings: 20160
      },
      reviews: [
        {
          studentId: { username: 'student1', firstName: 'John', lastName: 'Doe' },
          rating: 5,
          comment: 'Negin is amazing with children! My 8-year-old daughter looks forward to her Persian lessons every week. She makes learning fun and engaging.',
          sessionId: 'session1',
          createdAt: new Date('2024-01-15')
        },
        {
          studentId: { username: 'student2', firstName: 'Sarah', lastName: 'Smith' },
          rating: 5,
          comment: 'As a complete beginner, I was nervous about learning Persian. Negin\'s patient and encouraging approach made me feel comfortable from the first lesson.',
          sessionId: 'session2',
          createdAt: new Date('2024-01-10')
        }
      ],
      createdAt: new Date('2023-01-01'),
      updatedAt: new Date('2024-01-20')
    },
    '2': {
      _id: '2',
      userId: { username: 'salman', firstName: 'Salman', lastName: 'Zahabioun' },
      basicInfo: {
        fullName: 'Salman Zahabioun',
        email: 'support@pingquack.com',
        country: 'Iran',
        languages: ['Persian', 'English', 'French'],
        profilePicture: '/images/profiles/Salman Zahabioun.jpg'
      },
      teachingExperience: {
        yearsTeaching: 5,
        certifications: ['Persian Language Teaching Certificate', 'Online Teaching Specialist'],
        resume: 'PhD in Middle Eastern Studies, UCLA'
      },
      teachingOffer: {
        teachingStyle: 'Technology-enhanced learning with modern methods',
        targetStudents: ['Intermediate', 'Advanced', 'Academic students', 'Heritage speakers'],
        preferredSchedule: {
          days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
          timeSlots: ['10:00-14:00', '19:00-22:00'],
          timezone: 'America/Los_Angeles'
        }
      },
      rates: {
        hourlyRate: 30,
        currency: 'USD',
        offersFreeTrial: true,
        packages: [
          {
            name: 'Academic Pack',
            sessions: 8,
            price: 240,
            description: 'For academic and research purposes'
          }
        ]
      },
      profile: {
        bio: 'I bring a unique perspective to Persian language teaching, combining traditional methods with modern technology. My approach focuses on practical communication skills while building a strong foundation in grammar and culture.',
        subjects: ['Modern Persian', 'Persian Poetry', 'Iranian History', 'Persian Calligraphy', 'Academic Persian'],
        specializations: ['Academic Persian', 'Literature analysis', 'Historical context'],
        availability: {
          isAvailable: true
        }
      },
      stats: {
        totalSessions: 320,
        totalStudents: 89,
        averageRating: 4.8,
        totalReviews: 89,
        totalEarnings: 9600
      },
      reviews: [
        {
          studentId: { username: 'student3', firstName: 'Michael', lastName: 'Johnson' },
          rating: 5,
          comment: 'Amir\'s academic approach is perfect for my research needs. Highly recommended!',
          sessionId: 'session3',
          createdAt: new Date('2024-01-12')
        }
      ],
      createdAt: new Date('2023-03-01'),
      updatedAt: new Date('2024-01-18')
    },
    '3': {
      _id: '3',
      userId: { username: 'maryam', firstName: 'Maryam', lastName: 'Ghorbali' },
      basicInfo: {
        fullName: 'Maryam Ghorbali',
        email: 'maryam@pingquack.com',
        country: 'USA',
        languages: ['Persian', 'English', 'French'],
        profilePicture: '/images/profiles/maryam-ghorbali.jpg'
      },
      teachingExperience: {
        yearsTeaching: 6,
        certifications: ['TESOL Certified', 'Persian Language Teaching Certificate', 'Child Education Specialist'],
        resume: 'Bachelor\'s in Education, University of Toronto'
      },
      teachingOffer: {
        teachingStyle: 'Patient and encouraging approach with interactive activities',
        targetStudents: ['Beginner', 'Children', 'Families', 'Young learners'],
        preferredSchedule: {
          days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
          timeSlots: ['09:00-17:00'],
          timezone: 'America/Toronto'
        }
      },
      rates: {
        hourlyRate: 30,
        currency: 'USD',
        offersFreeTrial: true,
        packages: [
          {
            name: 'Family Pack',
            sessions: 6,
            price: 120,
            description: 'Perfect for families learning together'
          }
        ]
      },
      profile: {
        bio: 'I specialize in teaching Persian to beginners and children. My teaching style is patient, encouraging, and focused on building confidence. I use games, songs, and interactive activities to make learning fun and effective.',
        subjects: ['Beginner Persian', 'Children\'s Persian', 'Persian for Families', 'Basic Grammar', 'Cultural Activities'],
        specializations: ['Children\'s education', 'Family learning', 'Interactive methods'],
        availability: {
          isAvailable: true
        }
      },
      stats: {
        totalSessions: 580,
        totalStudents: 156,
        averageRating: 4.7,
        totalReviews: 156,
        totalEarnings: 12760
      },
      reviews: [
        {
          studentId: { username: 'student4', firstName: 'Emma', lastName: 'Wilson' },
          rating: 5,
          comment: 'Leila is amazing with kids! My daughter loves her Persian lessons.',
          sessionId: 'session4',
          createdAt: new Date('2024-01-14')
        }
      ],
      createdAt: new Date('2023-02-01'),
      updatedAt: new Date('2024-01-19')
    },

    '4': {
      _id: '4',
      userId: { username: 'shera', firstName: 'Shera', lastName: 'Malayeri' },
      basicInfo: {
        fullName: 'Shera Malayeri',
        email: 'support@pingquack.com',
        country: 'USA',
        languages: ['Persian', 'English', 'Spanish']
      },
      teachingExperience: {
        yearsTeaching: 4,
        certifications: ['TESOL Certified', 'Persian Language Teaching Certificate', 'Online Teaching Specialist'],
        resume: 'Master\'s in Applied Linguistics, University of Melbourne'
      },
      teachingOffer: {
        teachingStyle: 'Modern and engaging approach with technology integration',
        targetStudents: ['Beginner', 'Intermediate', 'Travelers', 'Language enthusiasts'],
        preferredSchedule: {
          days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
          timeSlots: ['08:00-16:00', '19:00-22:00'],
          timezone: 'Australia/Sydney'
        }
      },
      rates: {
        hourlyRate: 28,
        currency: 'USD',
        offersFreeTrial: true,
        packages: [
          {
            name: 'Travel Pack',
            sessions: 4,
            price: 100,
            description: 'Essential phrases for travelers'
          }
        ]
      },
      profile: {
        bio: 'I believe that learning Persian should be enjoyable and accessible. I use modern teaching methods and technology to create engaging lessons that help students progress quickly while having fun.',
        subjects: ['Conversational Persian', 'Persian for Travel', 'Modern Persian', 'Cultural Exchange', 'Language Exchange'],
        specializations: ['Travel Persian', 'Conversational skills', 'Cultural exchange'],
        availability: {
          isAvailable: true
        }
      },
      stats: {
        totalSessions: 290,
        totalStudents: 78,
        averageRating: 4.6,
        totalReviews: 78,
        totalEarnings: 8120
      },
      reviews: [
        {
          studentId: { username: 'student6', firstName: 'Lisa', lastName: 'Garcia' },
          rating: 4,
          comment: 'Neda helped me prepare for my trip to Iran. Her travel-focused lessons were perfect!',
          sessionId: 'session6',
          createdAt: new Date('2024-01-13')
        }
      ],
      createdAt: new Date('2023-08-01'),
      updatedAt: new Date('2024-01-15')
    },
    '5': {
      _id: '5',
      userId: { username: 'bahar_erfanian', firstName: 'Bahar', lastName: 'Erfanian' },
      basicInfo: {
        fullName: 'Bahar Erfanian',
        email: 'bahar.erfanian@example.com',
        country: 'United States',
        languages: ['Persian (Native)', 'English (Fluent)', 'Technical English'],
        profilePicture: '/images/profiles/bahar-erfanian.jpg'
      },
      teachingExperience: {
        yearsTeaching: 3,
        certifications: [
          'Persian Student Association (PSA) Member',
          'Computer Science Teaching Assistant'
        ],
        education: 'Master of Science (MS), Computer Science - California State University, Long Beach (2013-2015), Bachelor of Science (BS), Computer Software Engineering - Mazandaran University of Science and Technology (2008-2013)',
        resume: 'https://example.com/resumes/bahar-erfanian.pdf'
      },
      teachingOffer: {
        teachingStyle: 'As a computer science professional with deep Persian cultural roots, I bring a unique perspective to Persian language teaching. My technical background helps me create structured, logical approaches to language learning while maintaining the beauty and richness of Persian culture.',
        targetStudents: ['Beginner', 'Intermediate', 'Advanced', 'Professional learners', 'Heritage speakers'],
        preferredSchedule: {
          days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
          timeSlots: ['18:00-22:00'],
          timezone: 'America/Los_Angeles'
        }
      },
      rates: {
        hourlyRate: 28,
        currency: 'USD',
        offersFreeTrial: true,
        packages: [
          {
            name: 'Literature Mastery',
            sessions: 8,
            price: 240,
            description: 'Deep dive into Persian classical and modern literature with cultural context'
          },
          {
            name: 'Advanced Grammar',
            sessions: 10,
            price: 300,
            description: 'Comprehensive advanced Persian grammar with literary examples'
          },
          {
            name: 'Cultural Immersion',
            sessions: 12,
            price: 360,
            description: 'Complete cultural and linguistic immersion program'
          }
        ]
      },
      profile: {
        bio: 'As a computer science professional with deep Persian cultural roots, I bring a unique perspective to Persian language teaching. My technical background helps me create structured, logical approaches to language learning while maintaining the beauty and richness of Persian culture.',
        subjects: ['Persian Grammar', 'Conversational Persian', 'Persian for Professionals', 'Iranian Culture', 'Persian Literature', 'Technical Persian'],
        specializations: ['Structured learning', 'Technology-enhanced', 'Professional focus', 'Cultural integration'],
        availability: {
          isAvailable: true
        }
      },
      stats: {
        totalSessions: 180,
        totalStudents: 45,
        averageRating: 4.8,
        totalReviews: 45,
        totalEarnings: 5040
      },
      reviews: [
        {
          studentId: { username: 'student7', firstName: 'Michael', lastName: 'Chen' },
          rating: 5,
          comment: 'Dr. Erfanian\'s expertise in Persian literature is unmatched. Her ability to explain complex literary concepts while teaching the language is remarkable.',
          sessionId: 'session7',
          createdAt: new Date('2024-01-20')
        },
        {
          studentId: { username: 'student8', firstName: 'Emma', lastName: 'Wilson' },
          rating: 5,
          comment: 'Bahar\'s cultural immersion approach helped me understand Persian not just as a language, but as a window into a rich cultural heritage.',
          sessionId: 'session8',
          createdAt: new Date('2024-01-18')
        },
        {
          studentId: { username: 'student9', firstName: 'David', lastName: 'Brown' },
          rating: 5,
          comment: 'The literature-based learning method is perfect for advanced students. Bahar\'s deep knowledge of Persian poetry and prose makes every lesson fascinating.',
          sessionId: 'session9',
          createdAt: new Date('2024-01-16')
        }
      ],
      createdAt: new Date('2023-06-01'),
      updatedAt: new Date('2024-01-20')
    }
  };

  const fetchTeacherProfile = async () => {
    try {
      setLoading(true);
      const actualTeacherId = mapTeacherId(teacherId || '');
      console.log('🔍 Version:', VERSION);
      console.log('🔍 Fetching teacher with ID:', actualTeacherId);
      console.log('🔍 API URL:', `${API_BASE_URL}/api/teachers/${actualTeacherId}`);
      console.log('🔍 Environment:', process.env.NODE_ENV);
      console.log('🔍 Window location:', window.location.hostname);
      
      const response = await fetch(`${API_BASE_URL}/api/teachers/${actualTeacherId}?t=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response ok:', response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 API Response data:', data);
        const teacherData = data.data || data;
        
        // Ensure the teacher data has all required fields with proper structure
        const processedTeacher = {
          ...teacherData,
          _id: unmapTeacherId(teacherData._id),
          userId: teacherData.userId || { username: 'unknown', firstName: 'Unknown', lastName: 'User' },
          basicInfo: {
            ...teacherData.basicInfo,
            languages: teacherData.basicInfo?.languages || teacherData.profile?.languages || ['Persian', 'English']
          },
          teachingExperience: {
            ...teacherData.teachingExperience,
            certifications: teacherData.teachingExperience?.certifications || []
          },
          teachingOffer: {
            ...teacherData.teachingOffer,
            preferredSchedule: {
              days: teacherData.teachingOffer?.preferredSchedule?.days || Object.keys(teacherData.teachingOffer?.availability?.schedule || {}),
              timeSlots: teacherData.teachingOffer?.preferredSchedule?.timeSlots || ['09:00-17:00'],
              timezone: teacherData.teachingOffer?.preferredSchedule?.timezone || teacherData.teachingOffer?.availability?.timezone || 'UTC'
            }
          },
          rates: {
            ...teacherData.rates,
            packages: teacherData.rates?.packages || []
          },
          profile: {
            ...teacherData.profile,
            specializations: teacherData.profile?.specializations || teacherData.profile?.teachingMethods || [],
            availability: {
              isAvailable: teacherData.profile?.availability?.isAvailable || teacherData.teachingOffer?.availability?.isAvailable || true
            }
          },
          stats: {
            ...teacherData.stats,
            totalStudents: teacherData.stats?.totalStudents || 0,
            totalEarnings: teacherData.stats?.totalEarnings || 0
          },
          reviews: teacherData.reviews || [],
          demoLesson: teacherData.demoLesson || {}
        };
        
        console.log('🔍 Processed teacher data:', processedTeacher);
        setTeacher(processedTeacher);
        setError(null);
      } else {
        console.log('🔍 API call failed, response status:', response.status);
        const errorText = await response.text();
        console.log('🔍 Error response:', errorText);
        
        // Fallback to mock data for now
        const mockTeacher = mockTeachers[teacherId || ''];
        if (mockTeacher) {
          console.log('🔍 Using mock data as fallback');
          setTeacher(mockTeacher);
          setError(null);
        } else {
          setError(`Teacher not found. Please check the teacher ID or try again later.`);
        }
      }
    } catch (error) {
      console.error('Error fetching teacher profile:', error);
      
      // Fallback to mock data for now
      const mockTeacher = mockTeachers[teacherId || ''];
      if (mockTeacher) {
        console.log('🔍 Using mock data as fallback due to error');
        setTeacher(mockTeacher);
        setError(null);
      } else {
        setError(`Failed to load teacher profile. Please try again later.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem('teacher-favorites') || '[]');
    const newFavorites = isFavorite
      ? favorites.filter((id: string) => id !== teacherId)
      : [...favorites, teacherId];
    
    localStorage.setItem('teacher-favorites', JSON.stringify(newFavorites));
    setIsFavorite(!isFavorite);
  };

  const handleBookSession = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    navigate(`/teacher-session-payment/${teacherId}`);
  };

  const getRatingStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <FaStar
        key={i}
        className={`w-5 h-5 ${
          i < Math.floor(rating)
            ? 'text-yellow-400'
            : i < rating
            ? 'text-yellow-400 opacity-50'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading teacher profile...</p>
        </div>
      </div>
    );
  }

  if (error || !teacher) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {error || 'Teacher not found'}
          </h3>
          <button
            onClick={() => navigate('/teachers')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Teachers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/teachers')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <FaArrowLeft className="mr-2" />
              Back to Teachers
            </button>
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleFavorite}
                className={`p-2 rounded-full transition-colors ${
                  isFavorite
                    ? 'text-red-500 bg-red-50'
                    : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                }`}
              >
                <FaHeart className="w-5 h-5" />
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
                <FaShare className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Profile Header */}
            <div className="bg-white rounded-2xl shadow-sm border p-8 mb-8">
              <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-6">
                {/* Profile Picture */}
                <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-4xl font-bold shadow-lg">
                  {teacher.basicInfo.profilePicture ? (
                    <img
                      src={teacher.basicInfo.profilePicture}
                      alt={teacher.basicInfo.fullName}
                      className="w-32 h-32 rounded-full object-cover"
                    />
                  ) : (
                    teacher.basicInfo.fullName.charAt(0)
                  )}
                </div>

                {/* Profile Info */}
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h1 className="text-3xl font-bold text-gray-900">
                      {teacher.basicInfo.fullName}
                    </h1>
                    {teacher.stats.averageRating >= 4.5 && (
                      <FaAward className="text-yellow-500 w-6 h-6" title="Top Rated Teacher" />
                    )}
                  </div>
                  
                  <div className="flex items-center text-gray-600 mb-3">
                    <FaMapMarkerAlt className="mr-2" />
                    <span>{teacher.basicInfo.country}</span>
                    <span className="mx-2">•</span>
                    <FaGraduationCap className="mr-2" />
                    <span>{teacher.teachingExperience.yearsTeaching} years teaching</span>
                  </div>

                  {/* Rating */}
                  <div className="flex items-center mb-4">
                    <div className="flex items-center mr-3">
                      {getRatingStars(teacher.stats.averageRating)}
                    </div>
                    <span className="text-lg font-semibold text-gray-900">
                      {teacher.stats.averageRating.toFixed(1)}
                    </span>
                    <span className="text-gray-600 ml-1">
                      ({teacher.stats.totalReviews} reviews)
                    </span>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center space-x-6 text-sm text-gray-600">
                    <div className="flex items-center">
                      <FaUsers className="mr-1" />
                      <span>{teacher.stats.totalStudents} students</span>
                    </div>
                    <div className="flex items-center">
                      <FaClock className="mr-1" />
                      <span>{teacher.stats.totalSessions} sessions</span>
                    </div>
                    <div className="flex items-center">
                      <FaTrophy className="mr-1" />
                      <span>{teacher.stats.totalEarnings > 0 ? `$${teacher.stats.totalEarnings.toLocaleString()}` : 'New'}</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col space-y-3 w-full md:w-auto">
                  <button
                    onClick={handleBookSession}
                    className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center justify-center"
                  >
                    <FaBook className="mr-2" />
                    Book Session
                  </button>
                  {teacher.rates.offersFreeTrial && (
                    <button className="px-8 py-3 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold flex items-center justify-center">
                      <FaPlay className="mr-2" />
                      Free Trial
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl shadow-sm border mb-8">
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-8">
                  <button
                    onClick={() => setActiveTab('about')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'about'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    About
                  </button>
                  <button
                    onClick={() => setActiveTab('reviews')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'reviews'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Reviews ({teacher.reviews.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('schedule')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'schedule'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Schedule
                  </button>
                </nav>
              </div>

              <div className="p-8">
                {activeTab === 'about' && (
                  <div className="space-y-8">
                    {/* Bio */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">About</h3>
                      <p className="text-gray-700 leading-relaxed">
                        {teacher.profile.bio || "Experienced Persian language teacher with a passion for sharing Iranian culture and language."}
                      </p>
                    </div>

                    {/* Specializations */}
                    {teacher.profile.specializations && teacher.profile.specializations.length > 0 && (
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Specializations</h3>
                        <div className="flex flex-wrap gap-2">
                          {teacher.profile.specializations.map((spec, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                            >
                              {spec}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Subjects */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">Subjects</h3>
                      <div className="flex flex-wrap gap-2">
                        {teacher.profile.subjects.map((subject, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium"
                          >
                            {subject}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Languages */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">Languages</h3>
                      <div className="flex flex-wrap gap-2">
                        {teacher.basicInfo.languages.map((language, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium"
                          >
                            {language}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Teaching Style */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">Teaching Style</h3>
                      <p className="text-gray-700 leading-relaxed">
                        {teacher.teachingOffer.teachingStyle}
                      </p>
                    </div>

                    {/* Target Students */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">Ideal for Students</h3>
                      <div className="flex flex-wrap gap-2">
                        {teacher.teachingOffer.targetStudents.map((student, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium"
                          >
                            {student}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Certifications */}
                    {teacher.teachingExperience.certifications && teacher.teachingExperience.certifications.length > 0 && (
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Certifications</h3>
                        <div className="space-y-2">
                          {teacher.teachingExperience.certifications.map((cert, index) => (
                            <div key={index} className="flex items-center">
                              <FaCertificate className="text-blue-500 mr-2" />
                              <span className="text-gray-700">{cert}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Demo Lesson */}
                    {teacher.demoLesson && (
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Demo Lesson</h3>
                        {teacher.demoLesson.videoUrl && (
                          <div className="bg-gray-100 rounded-lg p-4 mb-3">
                            <div className="flex items-center">
                              <FaPlay className="text-blue-500 mr-2" />
                              <span className="text-blue-600 font-medium">Watch Demo Lesson</span>
                            </div>
                          </div>
                        )}
                        {teacher.demoLesson.description && (
                          <p className="text-gray-700">{teacher.demoLesson.description}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'reviews' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-semibold text-gray-900">Student Reviews</h3>
                      <div className="flex items-center">
                        <span className="text-2xl font-bold text-gray-900 mr-2">
                          {teacher.stats.averageRating.toFixed(1)}
                        </span>
                        <div className="flex items-center">
                          {getRatingStars(teacher.stats.averageRating)}
                        </div>
                      </div>
                    </div>

                    {teacher.reviews.length === 0 ? (
                      <div className="text-center py-8">
                        <FaStar className="text-gray-300 text-4xl mx-auto mb-4" />
                        <p className="text-gray-600">No reviews yet. Be the first to review this teacher!</p>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {teacher.reviews.map((review, index) => (
                          <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center mr-3">
                                  <span className="text-gray-600 font-medium">
                                    {review.studentId.firstName?.charAt(0) || review.studentId.username.charAt(0)}
                                  </span>
                                </div>
                                <div>
                                  <h4 className="font-medium text-gray-900">
                                    {review.studentId.firstName && review.studentId.lastName
                                      ? `${review.studentId.firstName} ${review.studentId.lastName}`
                                      : review.studentId.username}
                                  </h4>
                                  <div className="flex items-center">
                                    {getRatingStars(review.rating)}
                                  </div>
                                </div>
                              </div>
                              <span className="text-sm text-gray-500">
                                {formatDate(review.createdAt)}
                              </span>
                            </div>
                            <p className="text-gray-700 leading-relaxed">{review.comment}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'schedule' && (
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">Availability & Schedule</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Availability Status */}
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h4 className="font-semibold text-gray-900 mb-4">Current Status</h4>
                        <div className="flex items-center">
                          <div className={`w-3 h-3 rounded-full mr-3 ${
                            teacher.profile.availability.isAvailable ? 'bg-green-500' : 'bg-red-500'
                          }`}></div>
                          <span className="font-medium">
                            {teacher.profile.availability.isAvailable ? 'Available for sessions' : 'Currently unavailable'}
                          </span>
                        </div>
                        {teacher.profile.availability.nextAvailableDate && (
                          <p className="text-sm text-gray-600 mt-2">
                            Next available: {formatDate(teacher.profile.availability.nextAvailableDate)}
                          </p>
                        )}
                      </div>

                      {/* Timezone */}
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h4 className="font-semibold text-gray-900 mb-4">Timezone</h4>
                        <div className="flex items-center">
                          <FaGlobe className="text-blue-500 mr-2" />
                          <span>{teacher.teachingOffer.preferredSchedule.timezone}</span>
                        </div>
                      </div>
                    </div>

                    {/* Preferred Days */}
                    <div className="mt-6">
                      <h4 className="font-semibold text-gray-900 mb-4">Preferred Teaching Days</h4>
                      <div className="flex flex-wrap gap-2">
                        {teacher.teachingOffer.preferredSchedule.days.map((day, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium capitalize"
                          >
                            {day}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Time Slots */}
                    {teacher.teachingOffer.preferredSchedule.timeSlots.length > 0 && (
                      <div className="mt-6">
                        <h4 className="font-semibold text-gray-900 mb-4">Preferred Time Slots</h4>
                        <div className="flex flex-wrap gap-2">
                          {teacher.teachingOffer.preferredSchedule.timeSlots.map((slot, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium"
                            >
                              {slot}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Pricing Card */}
            <div className="bg-white rounded-2xl shadow-sm border p-6 mb-6 sticky top-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Pricing</h3>
              
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">Hourly Rate</span>
                  <span className="text-2xl font-bold text-gray-900">
                    ${teacher.rates.hourlyRate}
                  </span>
                </div>
                <div className="text-sm text-gray-500">
                  per hour • {teacher.rates.currency}
                </div>
              </div>

              {teacher.rates.packages && teacher.rates.packages.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Packages</h4>
                  <div className="space-y-3">
                    {teacher.rates.packages.map((pkg, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-gray-900">{pkg.name}</span>
                          <span className="font-bold text-blue-600">${pkg.price}</span>
                        </div>
                        <div className="text-sm text-gray-600 mb-2">
                          {pkg.sessions} sessions
                        </div>
                        {pkg.description && (
                          <p className="text-xs text-gray-500">{pkg.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {teacher.rates.offersFreeTrial && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <FaCheckCircle className="text-green-500 mr-2" />
                    <span className="text-green-800 font-medium">Offers free trial session</span>
                  </div>
                </div>
              )}

              <button
                onClick={handleBookSession}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold mb-3"
              >
                Book Session
              </button>
              
              <button 
                onClick={() => {
                  if (teacher?.basicInfo?.email) {
                    const subject = encodeURIComponent(`Message for ${teacher.basicInfo.fullName} - Farsi Learning`);
                    const body = encodeURIComponent(`Hello ${teacher.basicInfo.fullName},\n\nI'm interested in learning Farsi and would like to discuss lessons with you.\n\nBest regards,`);
                    window.open(`mailto:${teacher.basicInfo.email}?subject=${subject}&body=${body}`, '_blank');
                  }
                }}
                className="w-full px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold"
              >
                Send Message
              </button>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-2xl shadow-sm border p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Stats</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaUsers className="text-blue-500 mr-3" />
                    <span className="text-gray-700">Students</span>
                  </div>
                  <span className="font-semibold text-gray-900">{teacher.stats.totalStudents}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaClock className="text-green-500 mr-3" />
                    <span className="text-gray-700">Sessions</span>
                  </div>
                  <span className="font-semibold text-gray-900">{teacher.stats.totalSessions}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaGraduationCap className="text-purple-500 mr-3" />
                    <span className="text-gray-700">Experience</span>
                  </div>
                  <span className="font-semibold text-gray-900">{teacher.teachingExperience.yearsTeaching} years</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaStar className="text-yellow-500 mr-3" />
                    <span className="text-gray-700">Rating</span>
                  </div>
                  <span className="font-semibold text-gray-900">{teacher.stats.averageRating.toFixed(1)}/5</span>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Contact</h3>
              
              <div className="space-y-3">
                <div className="flex items-center">
                  <FaEnvelope className="text-gray-400 mr-3" />
                  <span className="text-gray-700">{teacher.basicInfo.email}</span>
                </div>
                
                <div className="flex items-center">
                  <FaGlobe className="text-gray-400 mr-3" />
                  <span className="text-gray-700">{teacher.basicInfo.country}</span>
                </div>
                
                <div className="flex items-center">
                  <FaLanguage className="text-gray-400 mr-3" />
                  <span className="text-gray-700">{teacher.basicInfo.languages.join(', ')}</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">Follow</h4>
                <div className="flex space-x-3">
                  <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors">
                    <FaLinkedin className="w-5 h-5" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-50 rounded-full transition-colors">
                    <FaTwitter className="w-5 h-5" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-pink-600 hover:bg-pink-50 rounded-full transition-colors">
                    <FaInstagram className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherProfile; 